import axios from 'axios';
import apiClient from '@/services/axios.js';
import {
    setTokens,
    saveUsuarioLogado,
    saveNomeUsuario,
    saveIsAdmin,
    saveTrocarSenha,
    savePerfis,
    saveCpf,
    saveCrm,
    saveUsuarioId,
    saveCandidatoId,
    getRefreshToken,
    logout as logoutAuth,
} from '@/services/auth.js';
import {
    mockIndicadores,
    mockDocumentosExigidos,
} from '@/mocks/mockData.js';
import { extrairNumeros } from '@/shared/formatadores.js';
import { extrairMensagemDeErro } from '@/shared/formatadorErros.js';

const SITUACAO_EM_ANDAMENTO = 1;
const ETAPA_CONFERENCIA_DOCUMENTAL = 3;

function limparSessaoLocal() {
    logoutAuth();
}

function inscricaoDisponivelParaAnalise(situacao, etapaAtual) {
    return situacao !== SITUACAO_EM_ANDAMENTO || etapaAtual >= ETAPA_CONFERENCIA_DOCUMENTAL;
}

function normalizarDocumentos(documentos) {
    return (documentos || []).map((documento) => ({
        ...documento,
        tipoDocumentoId: Number(documento.tipoDocumentoId),
        status: documento.status,
    }));
}

function normalizarInscricaoResumo(item) {
    const presencaCursoConfirmada = !!item.presencaCursoConfirmada || item.etapaAtual > 5;
    return {
        id: item.id,
        protocolo: item.protocolo,
        editalId: item.editalId,
        editalNumero: item.editalNumero,
        vagaId: item.vagaId,
        candidatoId: item.candidatoId,
        nome: item.candidatoNome,
        cpf: item.candidatoCpf,
        crm: item.candidatoCrm,
        especialidade: item.candidatoEspecialidade,
        municipio: item.candidatoMunicipio,
        status: item.situacao,
        etapaAtual: item.etapaAtual,
        disponivelParaAnalise: inscricaoDisponivelParaAnalise(item.situacao, item.etapaAtual),
        dataInscricao: item.inscritaEm,
        presencaCursoConfirmada,
        presencaCursoConfirmadaEm: item.presencaCursoConfirmadaEm || null,
        presencaCursoConfirmadaPor: item.presencaCursoConfirmadaPor || '',
        nivelamentoConcluido: presencaCursoConfirmada,
        documentos: normalizarDocumentos(item.documentos),
        pontuacaoFormacao: Number(item.pontuacaoFormacao) || 0,
        formacoesAptoTotal: Number(item.formacoesAptoTotal) || 0,
        formacoesAptoPontuadas: Number(item.formacoesAptoPontuadas) || 0,
        pareceres: item.pareceres || [],
        parecer: '',
        aceiteTermo: null,
        pagamento: null,
        revisaoPrimeiraEtapaSolicitada: !!item.revisaoPrimeiraEtapaSolicitada,
        revisaoPrimeiraEtapaJustificativa: item.revisaoPrimeiraEtapaJustificativa || '',
        pontuacaoDocumentalPublicada: item.pontuacaoDocumentalPublicada ?? null,
        pontuacaoDocumentalPublicadaEm: item.pontuacaoDocumentalPublicadaEm || null,
        pontuacaoDocumentalPublicadaPor: item.pontuacaoDocumentalPublicadaPor || '',
    };
}

function normalizarInscricaoDetalhe(item) {
    const pareceresDecisivos = (item.pareceres || []).filter(p => p.decisao === 1 || p.decisao === 2);
    const ultimoParecer = pareceresDecisivos[pareceresDecisivos.length - 1];
    const presencaCursoConfirmada = !!item.presencaCursoConfirmada || item.etapaAtual > 5;
    return {
        id: item.id,
        candidatoId: item.candidatoId,
        editalId: item.editalId,
        vagaId: item.vagaId,
        protocolo: item.protocolo,
        status: item.situacao,
        etapaAtual: item.etapaAtual,
        dataInscricao: item.inscritaEm,
        disponivelParaAnalise: inscricaoDisponivelParaAnalise(item.situacao, item.etapaAtual),
        presencaCursoConfirmada,
        presencaCursoConfirmadaEm: item.presencaCursoConfirmadaEm || null,
        presencaCursoConfirmadaPor: item.presencaCursoConfirmadaPor || '',
        nivelamentoConcluido: presencaCursoConfirmada,
        documentos: normalizarDocumentos(item.documentos),
        pareceres: item.pareceres || [],
        parecer: ultimoParecer ? ultimoParecer.justificativa : '',
        aceiteTermo: item.aceiteTermo,
        pagamento: item.pagamento,
        revisaoPrimeiraEtapaSolicitada: !!item.revisaoPrimeiraEtapaSolicitada,
        revisaoPrimeiraEtapaJustificativa: item.revisaoPrimeiraEtapaJustificativa || '',
        pontuacaoDocumentalPublicada: item.pontuacaoDocumentalPublicada ?? null,
        pontuacaoDocumentalPublicadaEm: item.pontuacaoDocumentalPublicadaEm || null,
        pontuacaoDocumentalPublicadaPor: item.pontuacaoDocumentalPublicadaPor || '',
    };
}

function selecionarInscricaoMaisRecente(inscricoes) {
    if (!inscricoes || inscricoes.length === 0) return null;
    return [...inscricoes].sort((a, b) => new Date(b.inscritaEm) - new Date(a.inscritaEm))[0];
}

function registrarSessao(commit, {
    id = '', accessToken, refreshToken, refreshTokenExpiresAt,
    isAdmin = false, trocarSenha = false, perfis = [], nome, email, cpf = '', crm = '', candidatoId = null,
}) {
    setTokens(accessToken, refreshToken, refreshTokenExpiresAt);
    saveUsuarioId(id);
    saveCandidatoId(candidatoId);
    saveUsuarioLogado(email);
    saveNomeUsuario(nome || email);
    saveIsAdmin(isAdmin);
    saveTrocarSenha(trocarSenha);
    savePerfis(perfis);
    saveCpf(cpf);
    saveCrm(crm);

    commit('SET_USUARIO', {
        id, nome: nome || email, email, isAdmin, trocarSenha, perfis, cpf, crm, candidatoId,
    });
}

export default {
    async efetuarLogin({ commit }, { email, password, rememberMe }) {
        const response = await apiClient.post('Usuario/Login', { email, password });
        registrarSessao(commit, { ...response.data, email });

        if (rememberMe) {
            localStorage.setItem('email', email);
        } else {
            localStorage.removeItem('email');
        }
    },

    async efetuarLogout({ commit }) {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
            try {
                await apiClient.post('Usuario/logout', { refreshToken });
            } catch {
            }
        }
        limparSessaoLocal();
        commit('LIMPAR_USUARIO');
        commit('LIMPAR_CONTEXTO_INSCRICAO');
    },

    async recuperarInscricaoAtualCandidato({ commit, state }) {
        const { candidatoId } = state.usuario;
        if (!candidatoId) {
            commit('SET_CONTEXTO_INSCRICAO', null);
            return null;
        }

        const response = await apiClient.get(`inscricoes/candidato/${candidatoId}`);
        const inscricaoAtual = selecionarInscricaoMaisRecente(response.data);
        commit('SET_CONTEXTO_INSCRICAO', inscricaoAtual);
        return inscricaoAtual;
    },

    async verificarInscricaoNoEdital({ commit, state }, editalId) {
        const { candidatoId } = state.usuario;
        if (!candidatoId) return null;

        const response = await apiClient.get(`inscricoes/candidato/${candidatoId}`);
        const inscricaoDoEdital = response.data.find((inscricao) => inscricao.editalId === editalId) || null;
        if (inscricaoDoEdital) commit('SET_CONTEXTO_INSCRICAO', inscricaoDoEdital);
        return inscricaoDoEdital;
    },

    async verificarUsuario(_, { email }) {
        try {
            await apiClient.post('Usuario/verificarUsuario', { email });
            return true;
        } catch (error) {
            if (error.response?.status === 404) return false;
            throw error;
        }
    },

    async solicitarRedefinicaoSenha(_, { email }) {
        await apiClient.post('Usuario/esqueci-senha', { email });
    },

    async redefinirSenha(_, { email, token, novaSenha }) {
        await apiClient.post('Usuario/redefinir-senha', { email, token, novaSenha });
    },

    async cadastrarContaCandidato(_, dados) {
        try {
            const response = await apiClient.post('Usuario/CriarConta', dados);
            return response.data;
        } catch (error) {
            throw extrairMensagemDeErro(error, 'NÃ£o foi possÃ­vel criar a conta.');
        }
    },

    async registrarAceiteTermos({ commit, state }, aceiteTermos) {
        if (aceiteTermos) {
            const { inscricaoId } = state.minhaInscricao;
            await apiClient.post(`inscricoes/${inscricaoId}/aceite-termo`, {
                versaoTermo: '1.0',
                userAgent: navigator.userAgent,
            });
        }
        commit('SET_ACEITE_TERMOS', aceiteTermos);
        return aceiteTermos;
    },

    escolherEdital({ commit }, editalId) {
        commit('SET_EDITAL_ESCOLHIDO', editalId);
    },

    async enviarArquivo(_, arquivo) {
        const formData = new FormData();
        formData.append('arquivo', arquivo);

        const response = await apiClient.post('arquivos', formData, {
            headers: { 'Content-Type': undefined },
        });
        return response.data;
    },

    async obterArquivoEdital(_, edital) {
        if (!edital.arquivo?.chave) throw new Error('Este edital nÃ£o tem arquivo anexado.');

        const response = await apiClient.get(`editais/${edital.id}/arquivo`, { responseType: 'blob' });
        return { url: URL.createObjectURL(response.data), nomeOriginal: edital.arquivo.nomeOriginal };
    },

    async obterAnexoEdital(_, { editalId, numeroAnexo, nomeOriginal }) {
        const response = await apiClient.get(`editais/${editalId}/anexos/${numeroAnexo}`, { responseType: 'blob' });
        return { url: URL.createObjectURL(response.data), nomeOriginal: nomeOriginal || `anexo-${numeroAnexo}` };
    },

    async obterArquivoDocumento(_, arquivo) {
        if (!arquivo?.chave) throw new Error('Arquivo nÃ£o possui identificador vÃ¡lido.');

        const params = new URLSearchParams();
        if (arquivo.nomeOriginal) params.append('nomeOriginal', arquivo.nomeOriginal);

        const query = params.toString();
        const caminho = query
            ? `arquivos/${encodeURIComponent(arquivo.chave)}?${query}`
            : `arquivos/${encodeURIComponent(arquivo.chave)}`;

        const response = await apiClient.get(caminho, { responseType: 'blob' });
        return { url: URL.createObjectURL(response.data), nomeOriginal: arquivo.nomeOriginal };
    },

    async selecionarEspecialidades({ commit }) {
        const response = await apiClient.get('especialidades');
        commit('SET_ESPECIALIDADES', response.data);
        return response.data;
    },

    async selecionarAreasAtuacao({ commit }) {
        const response = await apiClient.get('areas-atuacao');
        commit('SET_AREAS_ATUACAO', response.data);
        return response.data;
    },

    async definirEspecialidadeCandidato({ state }, { especialidadeId, areaAtuacaoId, municipioVagaPretendida }) {
        const { candidatoId } = state.usuario;
        await apiClient.put(`candidatos/${candidatoId}/especialidade`, {
            especialidadeId,
            areaAtuacaoId,
            municipioVagaPretendida,
        });
    },

    async iniciarInscricao({ commit, state }, { editalId, vagaId }) {
        const { candidatoId } = state.usuario;
        const response = await apiClient.post('inscricoes', { candidatoId, editalId, vagaId });
        commit('SET_INSCRICAO_ATUAL', { ...response.data, vagaId });
        return response.data;
    },

    async selecionarCandidato(_, id) {
        const response = await apiClient.get(`candidatos/${id}`);
        return response.data;
    },

    async selecionarDadosTasyPorCpf(_, cpf) {
        try {
            const response = await apiClient.get(`tasy/pessoa-fisica/${cpf}`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) return null;
            throw error;
        }
    },

    async atualizarDadosPessoaisCandidato(_, { candidatoId, ...dados }) {
        await apiClient.put(`candidatos/${candidatoId}/dados-pessoais`, dados);
    },

    async atualizarContatoCandidato(_, { candidatoId, ...dados }) {
        await apiClient.put(`candidatos/${candidatoId}/contato`, dados);
    },

    async atualizarEnderecoResidencialCandidato(_, { candidatoId, ...endereco }) {
        await apiClient.put(`candidatos/${candidatoId}/endereco-residencial`, endereco);
    },

    async adicionarLocalAtendimento(_, { candidatoId, ...local }) {
        const response = await apiClient.post(`candidatos/${candidatoId}/locais-atendimento`, local);
        return response.data.id;
    },

    async removerLocalAtendimento(_, { candidatoId, localAtendimentoId }) {
        await apiClient.delete(`candidatos/${candidatoId}/locais-atendimento/${localAtendimentoId}`);
    },

    async confirmarCadastroInscricao({ dispatch }, inscricaoId) {
        await apiClient.post(`inscricoes/${inscricaoId}/confirmar-cadastro`);
        await dispatch('recuperarInscricaoAtualCandidato');
    },

    async adicionarFormacaoCandidato(_, { candidatoId, ...formacao }) {
        const response = await apiClient.post(`candidatos/${candidatoId}/formacoes`, formacao);
        return response.data.id;
    },

    async removerFormacaoCandidato(_, { candidatoId, formacaoId }) {
        await apiClient.delete(`candidatos/${candidatoId}/formacoes/${formacaoId}`);
    },

    async adicionarExperienciaProfissionalCandidato(_, { candidatoId, ...experiencia }) {
        const response = await apiClient.post(`candidatos/${candidatoId}/experiencias-profissionais`, experiencia);
        return response.data.id;
    },

    async removerExperienciaProfissionalCandidato(_, { candidatoId, experienciaProfissionalId }) {
        await apiClient.delete(`candidatos/${candidatoId}/experiencias-profissionais/${experienciaProfissionalId}`);
    },

    async selecionarInscricao(_, id) {
        const response = await apiClient.get(`inscricoes/${id}`);
        return response.data;
    },

    async adicionarDocumentoInscricao(_, { inscricaoId, ...dados }) {
        const response = await apiClient.post(`inscricoes/${inscricaoId}/documentos`, dados);
        return response.data.id;
    },

    async substituirDocumentoInscricao(_, { inscricaoId, documentoId, ...dados }) {
        await apiClient.put(`inscricoes/${inscricaoId}/documentos/${documentoId}`, dados);
    },

    async removerDocumentoInscricao(_, { inscricaoId, documentoId }) {
        await apiClient.delete(`inscricoes/${inscricaoId}/documentos/${documentoId}`);
    },

    async concluirEnvioDocumentacaoInscricao({ dispatch }, inscricaoId) {
        await apiClient.post(`inscricoes/${inscricaoId}/concluir-envio-documentacao`);
        await dispatch('recuperarInscricaoAtualCandidato');
    },

    async gerarPagamentoInscricao({ dispatch }, { inscricaoId, ...dados }) {
        await apiClient.post(`inscricoes/${inscricaoId}/pagamento`, dados);
        await dispatch('recuperarInscricaoAtualCandidato');
    },

    async confirmarPagamentoInscricao(_, { inscricaoId, ...dados }) {
        await apiClient.post(`inscricoes/${inscricaoId}/pagamento/confirmar`, dados);
    },

    async registrarChegadaTeladePagamentoInscricao(_, inscricaoId) {
        try {
            await apiClient.post(`inscricoes/${inscricaoId}/registrar-chegada-pagamento`);
        } catch {
        }
    },

    async buscarEnderecoPorCep(_, cep) {
        const cepLimpo = extrairNumeros(cep);
        if (cepLimpo.length !== 8) throw new Error('CEP incompleto.');

        const { data } = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
        if (data.erro) throw new Error('CEP nÃ£o encontrado.');

        return {
            rua: data.logradouro,
            bairro: data.bairro,
            cidade: data.localidade,
            uf: data.uf,
        };
    },

    async salvarEtapaInscricao({ commit }, { etapa, dados, proximaEtapa }) {
        return new Promise((resolve) => {
            setTimeout(() => {
                commit('SET_ETAPA_INSCRICAO', { etapa, dados });
                if (proximaEtapa) commit('SET_ETAPA_ATUAL', proximaEtapa);
                resolve(dados);
            }, 300);
        });
    },

    async selecionarDocumentosExigidos({ commit }) {
        return new Promise((resolve) => {
            setTimeout(() => {
                commit('SET_DOCUMENTOS_EXIGIDOS', mockDocumentosExigidos);
                resolve(mockDocumentosExigidos);
            }, 300);
        });
    },

    async anexarDocumento({ commit }, { tipoId, arquivo }) {
        return new Promise((resolve) => {
            setTimeout(() => {
                const anexo = {
                    nome: arquivo.name,
                    tamanho: arquivo.size,
                    enviadoEm: new Date().toISOString(),
                };
                commit('SET_DOCUMENTO_ANEXADO', { tipoId, anexo });
                resolve(anexo);
            }, 400);
        });
    },

    excluirDocumento({ commit }, tipoId) {
        commit('EXCLUIR_DOCUMENTO_ANEXADO', tipoId);
    },

    async gerarPagamento({ commit }) {
        return new Promise((resolve) => {
            setTimeout(() => {
                const pagamento = {
                    valor: 350,
                    situacao: 'Aguardando pagamento',
                    vencimento: '2026-09-10',
                    linhaDigitavel: '34191.79001 01043.510047 91020.150008 9 12340000035000',
                };
                commit('SET_PAGAMENTO', pagamento);
                resolve(pagamento);
            }, 500);
        });
    },

    async selecionarIndicadores({ commit }) {
        return new Promise((resolve) => {
            setTimeout(() => {
                commit('SET_INDICADORES', mockIndicadores);
                resolve(mockIndicadores);
            }, 500);
        });
    },

    async selecionarTodasInscricoes({ commit }, {
        editalId, pageNumber = 1, pageSize = 30, termo = '', status = '',
    } = {}) {
        const response = await apiClient.get('inscricoes', {
            params: {
                editalId,
                pageNumber,
                pageSize,
                termo: termo || undefined,
                situacao: status === '' || status == null ? undefined : status,
            },
        });
        commit('SET_INSCRICOES', response.data.map(normalizarInscricaoResumo));

        if (response.headers.pagination) {
            const paginationData = JSON.parse(response.headers.pagination);
            commit('SET_PAGINATION', {
                currentPage: paginationData.currentPage,
                totalPages: paginationData.totalPages,
                pageSize: paginationData.itemsPerPage,
                totalItems: paginationData.totalItems,
            });
        }

        return response.data;
    },

    async selecionarInscricoesParaMetricas(_, { editalId } = {}) {
        const response = await apiClient.get('inscricoes', {
            params: { editalId, pageNumber: 1, pageSize: 1000 },
        });
        return response.data;
    },

    async selecionarInscricoesAguardandoHomologacao({ commit }, { editalId } = {}) {
        const response = await apiClient.get('inscricoes/aguardando-homologacao', {
            params: { editalId, pageNumber: 1, pageSize: 100 },
        });
        commit('SET_INSCRICOES', response.data.map(normalizarInscricaoResumo));
        return response.data;
    },

    async homologarInscricao(_, { inscricaoId, decisao, observacao }) {
        const response = await apiClient.post(`inscricoes/${inscricaoId}/homologar`, { decisao, observacao });
        return response.data;
    },

    async selecionarInscricaoDetalhe({ commit }, id) {
        const response = await apiClient.get(`inscricoes/${id}`);
        commit('ATUALIZAR_INSCRICAO', normalizarInscricaoDetalhe(response.data));
        return response.data;
    },

    async registrarParecerInscricao({ commit, state }, { id, decisao, justificativa }) {
        const DECISAO_PARECER = { Deferido: 1, Indeferido: 2 };
        await apiClient.post(`inscricoes/${id}/parecer`, {
            decisao: DECISAO_PARECER[decisao],
            justificativa,
            usuarioId: state.usuario.id,
            usuarioNome: state.usuario.nome || state.usuario.email,
        });
        const response = await apiClient.get(`inscricoes/${id}`);
        commit('ATUALIZAR_INSCRICAO', normalizarInscricaoDetalhe(response.data));
        return response.data;
    },

    async solicitarRevisaoPrimeiraEtapaInscricao({ commit, state }, { id, justificativa }) {
        await apiClient.post(`inscricoes/${id}/solicitar-revisao-primeira-etapa`, {
            justificativa,
            usuarioId: state.usuario.id,
            usuarioNome: state.usuario.nome || state.usuario.email,
        });
        const response = await apiClient.get(`inscricoes/${id}`);
        commit('ATUALIZAR_INSCRICAO', normalizarInscricaoDetalhe(response.data));
        return response.data;
    },

    async avaliarDocumentoInscricao({ commit, state }, {
        inscricaoId, documentoId, status, observacao,
    }) {
        await apiClient.post(`inscricoes/${inscricaoId}/documentos/${documentoId}/avaliar`, {
            status,
            observacao,
            usuarioId: state.usuario.id,
            usuarioNome: state.usuario.nome,
        });
        const response = await apiClient.get(`inscricoes/${inscricaoId}`);
        commit('ATUALIZAR_INSCRICAO', normalizarInscricaoDetalhe(response.data));
        return response.data;
    },

    async pontuarDocumentoInscricao({ commit, state }, {
        inscricaoId, documentoId, pontuacao, observacao,
    }) {
        await apiClient.post(`inscricoes/${inscricaoId}/documentos/${documentoId}/pontuar`, {
            pontuacao,
            observacao,
            usuarioId: state.usuario.id,
            usuarioNome: state.usuario.nome,
        });
        const response = await apiClient.get(`inscricoes/${inscricaoId}`);
        commit('ATUALIZAR_INSCRICAO', normalizarInscricaoDetalhe(response.data));
        return response.data;
    },

    async enviarPontuacaoDocumental({ commit }, id) {
        await apiClient.post(`inscricoes/${id}/pontuacao-documental/enviar`);
        const response = await apiClient.get(`inscricoes/${id}`);
        commit('ATUALIZAR_INSCRICAO', normalizarInscricaoDetalhe(response.data));
        return response.data;
    },

    async pontuarFormacaoCandidato({ commit, state }, { candidatoId, formacaoId, pontuacao }) {
        await apiClient.post(`candidatos/${candidatoId}/formacoes/${formacaoId}/pontuar`, { pontuacao });
        const response = await apiClient.get(`candidatos/${candidatoId}`);
        const formacoesApto = (response.data.formacoes || []).filter((formacao) => formacao.aptoPontuacao);
        const pontuadas = formacoesApto.filter((formacao) => formacao.pontuacao != null);
        const inscricao = (state.listaInscricoes || []).find((item) => Number(item.candidatoId) === Number(candidatoId));
        if (inscricao) {
            commit('ATUALIZAR_INSCRICAO', {
                ...inscricao,
                pontuacaoFormacao: pontuadas.reduce((soma, formacao) => soma + (Number(formacao.pontuacao) || 0), 0),
                formacoesAptoTotal: formacoesApto.length,
                formacoesAptoPontuadas: pontuadas.length,
            });
        }
        return response.data;
    },

    async registrarPresencaCursoInscricao({ commit }, id) {
        await apiClient.post(`inscricoes/${id}/registrar-presenca-curso`);
        const response = await apiClient.get(`inscricoes/${id}`);
        commit('ATUALIZAR_INSCRICAO', normalizarInscricaoDetalhe(response.data));
        return response.data;
    },

    async concluirCursoInscricao({ dispatch }, id) {
        return dispatch('registrarPresencaCursoInscricao', id);
    },

    async selecionarTodosEditais({ commit }) {
        const response = await apiClient.get('editais', { params: { pageNumber: 1, pageSize: 100 } });
        commit('SET_EDITAIS', response.data || []);
        return response.data || [];
    },

    async selecionarEditaisPublicados({ commit }) {
        const response = await apiClient.get('editais/publicados');
        commit('SET_EDITAIS', response.data || []);
        return response.data || [];
    },

    async selecionarTiposDocumento({ commit }) {
        const response = await apiClient.get('tipos-documento');
        commit('SET_TIPOS_DOCUMENTO', response.data);
        return response.data;
    },

    async selecionarCatalogoTiposDocumento({ commit }) {
        const response = await apiClient.get('tipos-documento/todos');
        commit('SET_CATALOGO_TIPOS_DOCUMENTO', response.data);
        return response.data;
    },

    async cadastrarTipoDocumento({ commit }, { descricao, exibirParaCandidato, limiteAnexos, aptoPontuacao }) {
        const response = await apiClient.post('tipos-documento', { descricao, exibirParaCandidato, limiteAnexos, aptoPontuacao });
        commit('ADICIONAR_TIPO_DOCUMENTO', response.data);
        return response.data;
    },

    async atualizarTipoDocumento({ commit }, {
        id, descricao, exibirParaCandidato, limiteAnexos, aptoPontuacao,
    }) {
        const response = await apiClient.put(`tipos-documento/${id}`, { descricao, exibirParaCandidato, limiteAnexos, aptoPontuacao });
        commit('ATUALIZAR_TIPO_DOCUMENTO', response.data);
        return response.data;
    },

    async atualizarSituacaoTipoDocumento({ commit }, { id, ativo }) {
        await apiClient.post(`tipos-documento/${id}/${ativo ? 'ativar' : 'inativar'}`);
        commit('ATUALIZAR_TIPO_DOCUMENTO', { id, ativo });
    },

    async cadastrarEdital({ commit }, dados) {
        const [anexo1, anexo2, anexo3, anexo4] = dados.anexos || [];
        const { data: edital } = await apiClient.post('editais', {
            numero: dados.numero,
            titulo: dados.titulo,
            arquivo: dados.arquivo,
            anexo1: anexo1 || null,
            anexo2: anexo2 || null,
            anexo3: anexo3 || null,
            anexo4: anexo4 || null,
            periodos: dados.periodos,
            documentosExigidos: dados.documentosExigidos,
            vagas: dados.vagas,
        });

        commit('ADICIONAR_EDITAL', edital);
        return edital;
    },

    async atualizarEdital({ commit }, { id, titulo, arquivo, anexos }) {
        const [anexo1, anexo2, anexo3, anexo4] = anexos || [];
        const response = await apiClient.put(`editais/${id}`, {
            titulo,
            arquivo,
            anexo1: anexo1 || null,
            anexo2: anexo2 || null,
            anexo3: anexo3 || null,
            anexo4: anexo4 || null,
        });
        commit('ATUALIZAR_EDITAL', response.data);
        return response.data;
    },

    async duplicarEdital({ commit }, { id, numero, arquivo }) {
        const response = await apiClient.post(`editais/${id}/duplicar`, { numero, arquivo });
        commit('ADICIONAR_EDITAL', response.data);
        return response.data;
    },

    async excluirEdital({ commit }, id) {
        await apiClient.delete(`editais/${id}`);
        commit('EXCLUIR_EDITAL', id);
        return id;
    },

    async publicarEdital({ commit }, id) {
        await apiClient.post(`editais/${id}/publicar`);
        commit('ATUALIZAR_EDITAL', { id, situacao: 'Aberto' });
    },

    async encerrarEdital({ commit }, id) {
        await apiClient.post(`editais/${id}/encerrar`);
        commit('ATUALIZAR_EDITAL', { id, situacao: 'Encerrado' });
    },

    async arquivarEdital({ commit }, id) {
        await apiClient.post(`editais/${id}/arquivar`);
        commit('ATUALIZAR_EDITAL', { id, situacao: 'Arquivado' });
    },

    async adicionarPeriodoEdital({ commit }, { editalId, ...periodo }) {
        await apiClient.post(`editais/${editalId}/periodos`, periodo);
        const { data } = await apiClient.get(`editais/${editalId}`);
        commit("ATUALIZAR_EDITAL", data);
        return data;
    },

    async atualizarPeriodoEdital({ commit }, { editalId, periodoId, ...periodo }) {
        await apiClient.put(`editais/${editalId}/periodos/${periodoId}`, periodo);
        const { data } = await apiClient.get(`editais/${editalId}`);
        commit("ATUALIZAR_EDITAL", data);
        return data;
    },

    async adicionarVagaEdital({ commit }, { editalId, ...vaga }) {
        await apiClient.post(`editais/${editalId}/vagas`, vaga);
        const { data } = await apiClient.get(`editais/${editalId}`);
        commit("ATUALIZAR_EDITAL", data);
        return data;
    },

    async atualizarVagaEdital({ commit }, { editalId, vagaId, ...vaga }) {
        await apiClient.put(`editais/${editalId}/vagas/${vagaId}`, vaga);
        const { data } = await apiClient.get(`editais/${editalId}`);
        commit("ATUALIZAR_EDITAL", data);
        return data;
    },

    async removerVagaEdital({ commit }, { editalId, vagaId }) {
        await apiClient.delete(`editais/${editalId}/vagas/${vagaId}`);
        const { data } = await apiClient.get(`editais/${editalId}`);
        commit("ATUALIZAR_EDITAL", data);
        return data;
    },

    async adicionarDocumentoExigidoEdital({ commit }, { editalId, ...documentoExigido }) {
        await apiClient.post(`editais/${editalId}/documentos-exigidos`, documentoExigido);
        const { data } = await apiClient.get(`editais/${editalId}`);
        commit("ATUALIZAR_EDITAL", data);
        return data;
    },

    async atualizarDocumentoExigidoEdital({ commit }, { editalId, documentoExigidoId, ...documentoExigido }) {
        await apiClient.put(`editais/${editalId}/documentos-exigidos/${documentoExigidoId}`, documentoExigido);
        const { data } = await apiClient.get(`editais/${editalId}`);
        commit("ATUALIZAR_EDITAL", data);
        return data;
    },

    async removerDocumentoExigidoEdital({ commit }, { editalId, documentoExigidoId }) {
        await apiClient.delete(`editais/${editalId}/documentos-exigidos/${documentoExigidoId}`);
        const { data } = await apiClient.get(`editais/${editalId}`);
        commit("ATUALIZAR_EDITAL", data);
        return data;
    },

    async selecionarUsuarios({ commit }) {
        const response = await apiClient.get('usuarios');
        commit('SET_USUARIOS', response.data);
        return response.data;
    },

    async criarUsuario({ commit }, { nome, email, perfis }) {
        const response = await apiClient.post('usuarios', { nome, email, perfis });
        commit('ADICIONAR_USUARIO', {
            id: response.data.id,
            nome: response.data.nome,
            email: response.data.email,
            perfis: response.data.perfis,
            ativo: true,
            trocarSenha: true,
            criadoEm: new Date().toISOString(),
        });
        return response.data;
    },

    async atualizarPerfisUsuario({ commit }, { id, perfis }) {
        await apiClient.put(`usuarios/${id}/perfis`, { perfis });
        commit('ATUALIZAR_USUARIO', { id, perfis });
    },

    async ativarUsuario({ commit }, id) {
        await apiClient.post(`usuarios/${id}/ativar`);
        commit('ATUALIZAR_USUARIO', { id, ativo: true });
    },

    async desativarUsuario({ commit }, id) {
        await apiClient.post(`usuarios/${id}/desativar`);
        commit('ATUALIZAR_USUARIO', { id, ativo: false });
    },

    async confirmarTrocaSenhaObrigatoria({ commit }, { senhaAtual, novaSenha }) {
        await apiClient.post('Usuario/change-password', { senhaAtual, novaSenha });
        saveTrocarSenha(false);
        commit('SET_TROCAR_SENHA', false);
    },

    async selecionarInscricoesComPagamentos({ commit }, { editalId, pageNumber = 1, pageSize = 30 } = {}) {
        const params = { pageNumber, pageSize };
        if (editalId) params.editalId = editalId;

        const response = await apiClient.get('inscricoes', { params });
        const pagamentos = response.data.map((item) => ({
            id: item.id,
            candidato: {
                id: item.candidatoId,
                nome: item.candidatoNome,
                cpf: item.candidatoCpf,
                crm: item.candidatoCrm,
            },
            edital: {
                id: item.editalId,
                titulo: item.editalNumero,
            },
            pagamento: item.pagamento,
        }));

        commit('SET_PAGAMENTOS', pagamentos);

        if (response.headers.pagination) {
            const paginationData = JSON.parse(response.headers.pagination);
            commit('SET_PAGINATION', {
                currentPage: paginationData.currentPage,
                totalPages: paginationData.totalPages,
                pageSize: paginationData.itemsPerPage,
                totalItems: paginationData.totalItems,
            });
        }

        return pagamentos;
    },

    async confirmarPagamento(_, { inscricaoId, baixadoEm }) {
        const response = await apiClient.post(`inscricoes/${inscricaoId}/pagamento/confirmar`, { baixadoEm });
        return response.data;
    },

    async selecionarPublicacoes(_, editalId) {
        const response = await apiClient.get(`editais/${editalId}/publicacoes`);
        return response.data;
    },

    async publicarLista(_, { editalId, titulo, arquivo }) {
        const response = await apiClient.post(`editais/${editalId}/publicacoes`, { titulo, arquivo });
        return response.data;
    },

    async removerPublicacao(_, { editalId, publicacaoId }) {
        await apiClient.delete(`editais/${editalId}/publicacoes/${publicacaoId}`);
    },

    async selecionarCandidatosAptos(_, editalId) {
        const response = await apiClient.get(`editais/${editalId}/publicacoes/candidatos`);
        return response.data;
    },
};
